import { useTranslation } from '../../../../lib/i18n';
import {
  PINTEREST_CAPTION_MARKS,
  PINTEREST_DESCRIPTION_LIMIT,
  PINTEREST_RENDITION,
  PINTEREST_TITLE_LIMIT,
  PINTEREST_UNMEASURED,
  pinterestGridRendition,
} from '../../../../lib/pinterest-post-preview';
import { postAuthorName, postCaptionDirection, postLinkUrl, postMediaGrid, postTitleAndBody } from '../../../../lib/social-post-look-parts';
import { socialBadgeColor } from '../../ui';
import { POST_LOOK_KEYS, PostAvatar, PostCaption, PostGrid, PostSingleMedia, useMediaLabel, type SocialPostLookProps } from './SocialPostLookParts';
import styles from './PinterestPostPreview.module.css';

function PinMark() {
  return (
    <svg className={styles.pinMark} width="36" height="36" viewBox="0 0 36 36" aria-hidden="true" focusable="false" data-testid="pinterest-pin-mark">
      <rect width="36" height="36" rx="18" fill={socialBadgeColor('PINTEREST')} />
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.9541 21.7028L11.8657 24.8527C11.6743 25.0479 11.3609 25.051 11.1656 24.8596L11.1621 24.8561C10.9663 24.6603 10.9648 24.3433 11.1586 24.1455L14.2471 20.9955L10.75 17.4971L11.3265 16.8573C12.3918 15.675 13.9086 15 15.5 15H17.25L19 13.25V12.4926C19 11.697 19.3161 10.9339 19.8787 10.3713L20.5 9.75L26.25 15.5L25.6287 16.1213C25.0661 16.6839 24.303 17 23.5074 17H22.75L21 18.75V20.5C21 22.0913 20.3246 23.6079 19.1418 24.6724L18.5 25.25L14.9541 21.7028ZM20 13.75L17.75 16H15.4526C14.2153 16 13.042 16.5495 12.25 17.5L18.5 23.75C19.4505 22.958 20 21.7847 20 20.5474V18.25L22.25 16H23.5429C23.9956 16 24.4299 15.8201 24.75 15.5L20.5 11.25C20.1799 11.5701 20 12.0044 20 12.4571V13.75Z"
      />
    </svg>
  );
}

function VisitArrow() {
  return (
    <svg className={styles.visitIcon} width="9" height="14" viewBox="0 0 9 14" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.7"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.9269 5.14864L1.82751 10.2477C1.66725 10.4079 1.40744 10.4079 1.2472 10.2476C1.08694 10.0874 1.08695 9.82757 1.24721 9.66732L6.34658 4.56833L1.5373 4.57299C1.31067 4.57299 1.12696 4.38927 1.12696 4.16264C1.12696 3.93602 1.31068 3.7523 1.5373 3.7523L7.74759 3.74763L7.7479 9.9629C7.7479 10.1895 7.56418 10.3732 7.33756 10.3732C7.11093 10.3732 6.92721 10.1895 6.92721 9.9629L6.9269 5.14864Z"
      />
    </svg>
  );
}

export function PinterestPostPreview({ card, onOpenMedia, onToggleCaption, onOpenLink }: SocialPostLookProps) {
  const { t } = useTranslation();
  const label = useMediaLabel(card);
  const grid = postMediaGrid(card);
  const { title, body } = postTitleAndBody(card.caption, PINTEREST_TITLE_LIMIT);
  const link = postLinkUrl(card.link);
  const name = postAuthorName(card);
  return (
    <>
      <div className={styles.mediaBox}>
        {grid.kind === 'single' && (
          <PostSingleMedia
            key={grid.tile.url}
            media={grid.tile}
            label={label(grid.tile, 0)}
            rendition={PINTEREST_RENDITION}
            className={styles.pin}
            testId="pinterest-media"
            unmeasured={PINTEREST_UNMEASURED}
            onOpen={() => onOpenMedia(grid.tile, 0)}
          />
        )}
        {grid.kind === 'grid' && (
          <PostGrid grid={grid} label={label} rendition={pinterestGridRendition} className={styles.pin} testId="pinterest-media" onOpen={onOpenMedia} />
        )}
        <PinMark />
        {link && (
          <a className={styles.visit} href={link} target="_blank" rel="noopener noreferrer" onClick={onOpenLink} data-testid="pinterest-link">
            <VisitArrow />
            <span className={styles.visitText}>{t(POST_LOOK_KEYS.visitSite)}</span>
          </a>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.profile}>
          <PostAvatar card={card} name={name} size={30} logoSize={18} />
          <span className={styles.name} data-testid="pinterest-profile-name">
            <bdi>{name}</bdi>
          </span>
        </div>
        {(title || body) && (
          <div className={styles.text}>
            {title && (
              <p className={styles.title} data-testid="pinterest-title">
                <span dir={postCaptionDirection(title) ?? 'auto'}>{title}</span>
              </p>
            )}
            {body && (
              <PostCaption
                caption={body}
                className={styles.description}
                testId="pinterest-description"
                cut={{ limit: PINTEREST_DESCRIPTION_LIMIT }}
                marks={PINTEREST_CAPTION_MARKS}
                onToggle={onToggleCaption}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
